/**
 * Nymia · /creator — Voce Pro landing (compact, voice-first).
 * Copy on i18n namespace `creator_page` (IT/EN). Palette: nero · oro · bianco caldo.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Gift, Radio, PhoneCall } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { trackPixelEvent } from '../hooks/useMetaPixel';
import AperitivoSection from '../components/AperitivoSection';
import { useLandingMetadata } from '../hooks/useLandingMetadata';
import { useAuth } from '../context/AuthContext';
import { hasActiveCreatorProfile } from '../lib/creatorPresentation';

const COLORS = {
  black: '#0A0A0A',
  warmWhite: '#F5F1E8',
  gold: '#D4AF37',
  shadow: '#6B6B6B',
};
const serif = { fontFamily: '"Playfair Display", Georgia, serif' };
const inter = { fontFamily: '"Inter", system-ui, sans-serif' };
const EASE = [0.22, 0.61, 0.36, 1];

const IMG = {
  hero:
    'https://static.prod-images.emergentagent.com/jobs/70cab226-6488-47ae-8a5f-b42665648727/images/4acd2404776de7efadcb3fad2f88f695161193df10b9e0c2b5926db16d31cc4f.jpeg',
  passion:
    'https://static.prod-images.emergentagent.com/jobs/70cab226-6488-47ae-8a5f-b42665648727/images/ad976a4f70809727420efb3769d988963986989b819401a7368b692f58a504de.jpeg',
};

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.12, ease: EASE } }),
};

const Reveal = ({ children, index = 0, className = '' }) => (
  <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} custom={index} className={className}>
    {children}
  </motion.div>
);

const GrainOverlay = () => (
  <div
    aria-hidden
    className="pointer-events-none fixed inset-0 z-[60] mix-blend-overlay"
    style={{
      opacity: 0.14,
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.9 0 0 0 0 0.9 0 0 0 0 0.85 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
    }}
  />
);

const useCreatorCtas = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const alreadyActive = hasActiveCreatorProfile(user);
  const goPrimary = (name) => () => {
    try { trackPixelEvent('Lead', { content_name: name }); } catch { /* pixel not loaded */ }
    navigate('/register?intent=creator');
  };
  const goExplore = () => {
    try { trackPixelEvent('ViewContent', { content_name: 'creator_landing_cta_explore' }); } catch { /* pixel not loaded */ }
  };
  return { alreadyActive, goPrimary, goExplore };
};

const PrimaryButton = ({ label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 rounded-full px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] transition-all duration-500 hover:gap-5 hover:brightness-110"
    style={{ ...inter, color: COLORS.black, backgroundColor: COLORS.gold }}
  >
    {label}
    <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
  </button>
);

// 1 · HERO
const Hero = ({ t }) => {
  const { alreadyActive, goPrimary } = useCreatorCtas();
  return (
    <section data-testid="creator-hero" className="relative overflow-hidden px-5 pb-14 pt-28 md:px-10 md:pb-20 md:pt-36" style={{ backgroundColor: COLORS.black }}>
      <div aria-hidden className="pointer-events-none absolute -left-40 top-10 h-[520px] w-[520px] rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.16) 0%, rgba(212,175,55,0) 65%)' }} />
      <div className="relative mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }}
            className="text-4xl leading-[1.05] sm:text-5xl lg:text-6xl"
            style={{ ...serif, fontStyle: 'italic', fontWeight: 500, color: COLORS.warmWhite, letterSpacing: '-0.02em' }}
            data-testid="creator-hero-title"
          >
            {t('hero.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.25, ease: EASE }}
            className="mt-5 text-lg md:text-xl"
            style={{ ...serif, fontStyle: 'italic', color: COLORS.gold }}
            data-testid="creator-hero-subtitle"
          >
            {t('hero.subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.45, ease: EASE }}
            className="mt-6 max-w-xl space-y-3 text-[15px] leading-relaxed md:text-base"
            style={{ ...inter, color: 'rgba(245,241,232,0.78)' }}
            data-testid="creator-hero-text"
          >
            <p>{t('hero.text_1')}</p>
            <p>{t('hero.text_2')}</p>
          </motion.div>
          {!alreadyActive && (
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: EASE }} className="mt-8">
              <PrimaryButton label={t('hero.cta')} onClick={goPrimary('creator_landing_cta_hero')} testId="creator-hero-cta" />
            </motion.div>
          )}
        </div>
        <motion.figure
          initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.2, ease: EASE }}
          className="relative overflow-hidden rounded-3xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
          data-testid="creator-hero-image"
        >
          <img src={IMG.hero} alt={t('hero.img_alt')} className="aspect-[4/3] w-full object-cover lg:aspect-[5/4]" />
          <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.55) 100%)' }} />
        </motion.figure>
      </div>
    </section>
  );
};

// 2 · OGNUNO HA QUALCOSA DA CONDIVIDERE
const Share = ({ t }) => (
  <section data-testid="creator-share" className="border-t border-white/5 px-5 py-16 md:px-10 md:py-20" style={{ backgroundColor: '#070707' }}>
    <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <Reveal className="relative order-2 lg:order-1">
        <figure className="overflow-hidden rounded-3xl border border-white/10" data-testid="creator-share-image">
          <img src={IMG.passion} alt={t('share.img_alt')} className="aspect-[4/3] w-full object-cover" loading="lazy" />
        </figure>
      </Reveal>
      <div className="order-1 lg:order-2">
        <Reveal>
          <h2 className="text-3xl leading-tight sm:text-4xl lg:text-5xl" style={{ ...serif, fontWeight: 500, color: COLORS.warmWhite, letterSpacing: '-0.015em' }} data-testid="creator-share-title">
            {t('share.title')}
          </h2>
        </Reveal>
        <Reveal index={1}>
          <p className="mt-5 text-lg md:text-xl" style={{ ...serif, fontStyle: 'italic', color: 'rgba(245,241,232,0.9)' }}>{t('share.text_1')}</p>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed md:text-base" style={{ ...inter, color: 'rgba(245,241,232,0.72)' }}>{t('share.text_2')}</p>
        </Reveal>
        <Reveal index={2}>
          <p className="mt-6 border-l-2 pl-4 text-xl md:text-2xl" style={{ ...serif, fontStyle: 'italic', fontWeight: 500, color: COLORS.gold, borderColor: COLORS.gold }} data-testid="creator-share-highlight">
            {t('share.highlight')}
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

// 3 · SCEGLI COME VALORIZZARE LA TUA VOCE
const WAY_ICONS = [Gift, Radio, PhoneCall];
const Ways = ({ t }) => {
  const cards = t('ways.cards', { returnObjects: true }) || [];
  return (
    <section data-testid="creator-ways" className="border-t border-white/5 px-5 py-16 md:px-10 md:py-20" style={{ backgroundColor: COLORS.black }}>
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <h2 className="text-3xl leading-tight sm:text-4xl lg:text-5xl" style={{ ...serif, fontWeight: 500, color: COLORS.warmWhite, letterSpacing: '-0.015em' }} data-testid="creator-ways-title">
            {t('ways.title')}
          </h2>
          <span className="mt-4 block h-[2px] w-20 rounded-full" style={{ backgroundColor: COLORS.gold }} />
        </Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {cards.map((c, i) => {
            const Icon = WAY_ICONS[i] || Gift;
            return (
              <Reveal key={c.title} index={i + 1}>
                <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors duration-500 hover:border-[#D4AF37]/50" data-testid={`creator-way-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border" style={{ borderColor: 'rgba(212,175,55,0.45)', backgroundColor: 'rgba(212,175,55,0.08)' }}>
                    <Icon className="h-5 w-5" strokeWidth={1.5} style={{ color: COLORS.gold }} />
                  </span>
                  <h3 className="mt-5 text-xl" style={{ ...serif, fontWeight: 500, color: COLORS.warmWhite }}>{c.title}</h3>
                  <p className="mt-2 text-[14.5px] leading-relaxed" style={{ ...inter, color: 'rgba(245,241,232,0.7)' }}>{c.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal index={4}>
          <p className="mt-10 text-xl md:text-2xl" style={{ ...serif, fontStyle: 'italic', fontWeight: 500, color: COLORS.gold }} data-testid="creator-ways-footer">
            {t('ways.footer')}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

// 4 · INIZIA SENZA OBBLIGHI
const Free = ({ t }) => (
  <section data-testid="creator-free" className="border-t border-white/5 px-5 py-16 md:px-10 md:py-20" style={{ backgroundColor: '#070707' }}>
    <div className="mx-auto max-w-3xl text-center">
      <Reveal>
        <h2 className="text-3xl leading-tight sm:text-4xl lg:text-5xl" style={{ ...serif, fontWeight: 500, color: COLORS.warmWhite, letterSpacing: '-0.015em' }} data-testid="creator-free-title">
          {t('free.title')}
        </h2>
      </Reveal>
      <Reveal index={1}>
        <p className="mt-6 text-lg md:text-xl" style={{ ...serif, fontStyle: 'italic', color: COLORS.gold }}>{t('free.l1')}</p>
        <p className="mt-3 text-[15px] leading-relaxed md:text-base" style={{ ...inter, color: 'rgba(245,241,232,0.78)' }}>{t('free.l2')}</p>
        <p className="mt-3 text-[15px] leading-relaxed md:text-base" style={{ ...inter, color: 'rgba(245,241,232,0.78)' }}>{t('free.l3')}</p>
        <p className="mt-3 text-sm" style={{ ...inter, color: 'rgba(245,241,232,0.55)' }} data-testid="creator-free-disclaimer">{t('free.l4')}</p>
      </Reveal>
      <Reveal index={2}>
        <Link to="/terms" className="mt-6 inline-block text-xs uppercase tracking-[0.25em] transition-colors hover:text-white" style={{ ...inter, color: 'rgba(212,175,55,0.8)', borderBottom: '1px solid rgba(212,175,55,0.4)', paddingBottom: 3 }} data-testid="creator-free-terms">
          {t('free.terms')}
        </Link>
      </Reveal>
    </div>
  </section>
);

// 5 · CTA FINALE
const Final = ({ t }) => {
  const { alreadyActive, goPrimary, goExplore } = useCreatorCtas();
  return (
    <section data-testid="creator-final" className="relative overflow-hidden border-t border-white/5 px-5 py-20 md:px-10 md:py-24" style={{ backgroundColor: COLORS.black }}>
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 65%)' }} />
      <div className="relative mx-auto max-w-3xl text-center">
        <Reveal>
          <h2 className="text-3xl leading-tight sm:text-4xl lg:text-5xl" style={{ ...serif, fontStyle: 'italic', fontWeight: 500, color: COLORS.gold, letterSpacing: '-0.02em' }} data-testid="creator-final-title">
            {t('final.title')}
          </h2>
        </Reveal>
        <Reveal index={1}>
          <p className="mt-4 text-lg md:text-xl" style={{ ...serif, fontStyle: 'italic', color: 'rgba(245,241,232,0.85)' }} data-testid="creator-final-subtitle">
            {t('final.subtitle')}
          </p>
        </Reveal>
        <Reveal index={2} className="mt-9 flex flex-col items-center justify-center gap-5 sm:flex-row sm:gap-8">
          {!alreadyActive && (
            <PrimaryButton label={t('final.cta_primary')} onClick={goPrimary('creator_landing_cta_primary')} testId="creator-cta-primary" />
          )}
          <Link
            to="/explore"
            onClick={goExplore}
            data-testid="creator-cta-secondary"
            className="text-xs uppercase tracking-[0.25em] transition-opacity duration-500 hover:opacity-100"
            style={{ ...inter, color: COLORS.warmWhite, opacity: 0.65, borderBottom: '1px solid rgba(245,241,232,0.35)', paddingBottom: 3 }}
          >
            {t('final.cta_secondary')}
          </Link>
        </Reveal>
        <Reveal index={3}>
          <p className="mt-14 text-xs uppercase tracking-[0.4em]" style={{ ...inter, color: 'rgba(212,175,55,0.75)' }} data-testid="creator-final-signature">
            {t('final.signature')}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

// PAGE ROOT
export default function CreatorPage() {
  const { user } = useAuth();
  useLandingMetadata(hasActiveCreatorProfile(user) ? 'creator_active' : 'creator');
  const { t } = useTranslation('creator_page');
  return (
    <div
      data-testid="creator-page"
      className="relative min-h-screen w-full overflow-x-hidden"
      style={{ backgroundColor: COLORS.black, color: COLORS.warmWhite }}
    >
      <GrainOverlay />

      {/* Chrome: logo + menu + language switcher */}
      <header
        className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#050505]/70 backdrop-blur-xl"
        data-testid="creator-header"
      >
        <div className="flex items-center justify-between px-5 py-3.5 md:px-10 md:py-4">
          <Link
            to="/"
            className="transition-opacity duration-500 hover:opacity-80"
            style={{ ...serif, fontSize: '1.35rem', fontWeight: 500, letterSpacing: '0.08em', color: COLORS.warmWhite, opacity: 0.85, textDecoration: 'none' }}
            data-testid="creator-header-logo"
          >
            Nymia
          </Link>
          <nav className="flex items-center gap-3 whitespace-nowrap md:gap-8" data-testid="creator-header-nav">
            <Link
              to="/"
              className="text-[12px] font-medium text-white/60 transition-colors hover:text-white md:text-[13px]"
              style={inter}
              data-testid="creator-nav-home"
            >
              {t('header.home')}
            </Link>
            <span
              className="text-[12px] font-semibold md:text-[13px]"
              style={{ ...inter, color: COLORS.gold }}
              data-testid="creator-nav-creator"
            >
              {t('header.creator')}
            </span>
            <Link
              to="/cosa-puoi-fare"
              className="hidden text-[12px] font-medium text-white/60 transition-colors hover:text-white sm:inline md:text-[13px]"
              style={inter}
              data-testid="creator-nav-cosa"
            >
              {t('header.cosa')}
            </Link>
            <Link
              to="/contact"
              className="text-[12px] font-medium text-white/60 transition-colors hover:text-white md:text-[13px]"
              style={inter}
              data-testid="creator-nav-contatti"
            >
              {t('header.contatti')}
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden md:block" style={{ opacity: 0.7 }}>
              <LanguageSwitcher variant="compact" testId="creator-lang-switcher" />
            </div>
            <Link
              to="/login"
              className="hidden rounded-full border border-white/15 px-4 py-1.5 text-[12px] font-semibold text-white transition-colors hover:border-white/40 sm:block"
              style={inter}
              data-testid="creator-header-login"
            >
              {t('header.login')}
            </Link>
            <Link
              to="/register?intent=creator"
              className="rounded-full px-4 py-1.5 text-[12px] font-bold text-black transition-opacity hover:opacity-90"
              style={{ ...inter, backgroundColor: COLORS.gold }}
              data-testid="creator-header-cta"
            >
              {t('header.cta')}
            </Link>
          </div>
        </div>
      </header>

      <main>
        <Hero t={t} />
        <Share t={t} />
        <Ways t={t} />
        <Free t={t} />
        {/* Aperitivo Nymia (gestito da Admin) */}
        <AperitivoSection source="creator" />
        <Final t={t} />
      </main>
    </div>
  );
}
